import { KitStatus } from '@/enums';
import { KitEntity } from '@/pages/privatePages/kit/types';
import {
  PIPELINE_TYPES,
  PipelineHistoryEntity,
  PipelineHistoryOutputAndError,
  PipelineType,
} from '@/types/pipeline-history';
import { KitAlertSeverity } from '../../enums';

export type QcFailureTag = 'RED' | 'YELLOW' | string;

export type QcFailure = {
  name: string;
  tags: QcFailureTag[];
  message: string;
  description: string;
};

export type KitFailureItem = {
  severity: KitAlertSeverity;
  title: string;
  detail: string;
  /** The QC pipeline-history record this failure was raised against. */
  pipelineHistoryId: string;
  /** Pipeline-history type this failure came from — set for FASTQ QC failures so callers can detect the bypass case. */
  type?: PipelineType;
  /** Raw output of the backing pipeline-history record — needed to bypass a FASTQ QC failure. */
  output?: PipelineHistoryOutputAndError | null;
};

type QcFailureWithHistory = {
  failure: QcFailure;
  pipelineHistoryId: string;
};

const getQcCompletePipelineHistories = (
  kit: KitEntity,
): PipelineHistoryEntity[] =>
  (kit.activeSequence?.pipelineEntity?.pipelineHistories ?? []).filter(
    history => history.type === PIPELINE_TYPES.PDF_QC_V5_COMPLETE,
  );

const getPdfQcFailures = (kit: KitEntity): QcFailureWithHistory[] =>
  getQcCompletePipelineHistories(kit).flatMap(history =>
    ((history.error?.details?.failures as QcFailure[] | undefined) ?? []).map(
      failure => ({ failure, pipelineHistoryId: history.id }),
    ),
  );

export type KitInvestigator = {
  id: string;
  firstName?: string;
  lastName?: string;
};

// Most recently logged incident-alert-logger record against the kit's
// QC-complete history — backs both the investigator display and the
// "Set Active" delete target for ignored kits.
const getLatestAlertLogger = (kit: KitEntity) => {
  const pdfQcErroredHistories = getQcCompletePipelineHistories(kit);
  const [history] = pdfQcErroredHistories?.length
    ? pdfQcErroredHistories
    : getFastqQcCompletePipelineHistories(kit);
  const alertLoggers = (history?.incidents ?? []).flatMap(
    incident => incident.alertLoggers ?? [],
  );

  return alertLoggers[alertLoggers.length - 1];
};

// Investigator shown is whoever most recently logged against the kit's QC-complete history.
export const getKitInvestigator = (
  kit: KitEntity,
): KitInvestigator | undefined => getLatestAlertLogger(kit)?.investigator;

// id of the latest incident-alert-logger record — the DELETE target for
// "Set Active" on an ignored kit.
export const getKitIncidentAlertLoggerId = (
  kit: KitEntity,
): string | undefined => getLatestAlertLogger(kit)?.id;

const getFailureSeverity = (failure: QcFailure): KitAlertSeverity | null => {
  if (failure.tags?.includes('RED')) return KitAlertSeverity.CRITICAL;
  if (failure.tags?.includes('YELLOW')) return KitAlertSeverity.WARNINGS;
  return null;
};

const getFastqQcCompletePipelineHistories = (
  kit: KitEntity,
): PipelineHistoryEntity[] =>
  (kit.activeSequence?.pipelineEntity?.pipelineHistories ?? []).filter(
    history =>
      (history.type === PIPELINE_TYPES.FASTQ_QC_SAMPLE_1_COMPLETE ||
        history.type === PIPELINE_TYPES.FASTQ_QC_SAMPLE_2_COMPLETE) &&
      history.error != null,
  );

const hasFastqQcSampleError = (kit: KitEntity): boolean =>
  getFastqQcCompletePipelineHistories(kit).length > 0;

// FASTQ QC sample errors have no RED/YELLOW tags like PDF_QC failures do —
// error.details is a map of file -> error code(s), but the shape of the
// value has changed across records: a plain string (e.g. "Error"), an array
// of code strings (e.g. ["SEQUENCE_LENGTH_NOT_ENOUGH_LENGTH"]), or an array
// of { key } objects (e.g. [{ key: "PER_BASE_SEQUENCE_QUALITY_FAILED" }]) —
// normalize all three to a single display string.
// A SEQUENCING kit carrying one of these is always WARNINGS (see getKitAlertSeverity).
type FastqQcErrorCode = string | { key: string };

const getFastqQcErrors = (
  value: string | FastqQcErrorCode[] | undefined,
): string => {
  if (value == null) return '';
  if (!Array.isArray(value)) return String(value);
  return value
    .map(code => (typeof code === 'string' ? code : code?.key))
    .filter(Boolean)
    .join(', ');
};

const getFastqQcFailureItems = (kit: KitEntity): KitFailureItem[] =>
  getFastqQcCompletePipelineHistories(kit).flatMap(history => {
    const details =
      (history.error?.details as
        | Record<string, string | FastqQcErrorCode[]>
        | undefined) ?? {};
    return Object.entries(details).map(([file, codes]) => ({
      severity: KitAlertSeverity.WARNINGS,
      title: getFastqQcErrors(codes),
      detail: file,
      pipelineHistoryId: history.id,
      type: history.type,
      output: history.output,
    }));
  });

// A kit can carry multiple QC failures — its severity is the worst one found (RED beats YELLOW).
// Beyond QC-failure tags, a kit's currentStatus can itself force a severity:
// REPORT_ERROR always means CRITICAL; REPORT_UNDER_SUPPORT means WARNINGS, as
// does a SEQUENCING kit whose FASTQ QC sample pipeline step recorded an error.
export const getKitAlertSeverity = (
  kit: KitEntity,
): KitAlertSeverity | null => {
  const severities = getPdfQcFailures(kit).map(({ failure }) =>
    getFailureSeverity(failure),
  );
  const status = kit.currentStatus?.status;

  if (
    severities.includes(KitAlertSeverity.CRITICAL) ||
    status === KitStatus.REPORT_ERROR
  )
    return KitAlertSeverity.CRITICAL;

  if (
    (severities.includes(KitAlertSeverity.WARNINGS) &&
      status === KitStatus.REPORT_UNDER_SUPPORT) ||
    (status === KitStatus.SEQUENCING && hasFastqQcSampleError(kit))
  )
    return KitAlertSeverity.WARNINGS;

  return null;
};

export const getKitFailureItems = (kit: KitEntity): KitFailureItem[] => [
  ...getPdfQcFailures(kit)
    .map(({ failure, pipelineHistoryId }) => ({
      failure,
      pipelineHistoryId,
      severity: getFailureSeverity(failure),
    }))
    .filter(
      (
        entry,
      ): entry is {
        failure: QcFailure;
        pipelineHistoryId: string;
        severity: KitAlertSeverity;
      } => entry.severity !== null,
    )
    .map(({ failure, pipelineHistoryId, severity }) => ({
      severity,
      title: failure.message,
      detail: failure.description,
      pipelineHistoryId,
    })),
  ...getFastqQcFailureItems(kit),
];
